import React from 'react';
import { TimeValue } from './types';
import './TimeSelector.scss';
import { ReactComponent as ChevronDownIcon } from '../../images/chevron-down.svg';

interface CustomSelectProps {
  value: string;
  options: string[];
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}

interface TimeSelectorProps {
  startTime: TimeValue;
  endTime: TimeValue;
  onStartTimeChange: (time: TimeValue) => void;
  onEndTimeChange: (time: TimeValue) => void;
  mode: 'start' | 'end' | 'both';
}

const CustomSelect: React.FC<CustomSelectProps> = ({ value, options, onChange }) => {
  return (
    <div className='custom-select-wrapper'>
      <select className='time-select' value={value} onChange={onChange}>
        {options.map((option) => (
          <option key={option} value={option} className='time-option'>
            {option}
          </option>
        ))}
      </select>
      <span className='custom-select-icon'>
        <ChevronDownIcon />
      </span>
    </div>
  );
};

const TimeSelector: React.FC<TimeSelectorProps> = ({
  startTime,
  endTime,
  onStartTimeChange,
  onEndTimeChange,
  mode,
}) => {
  const hours = Array.from({ length: 12 }, (_, i) => String(i === 0 ? 12 : i).padStart(2, '0'));
  const minutes = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));
  const seconds = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

  const handleStartHoursChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onStartTimeChange({ ...startTime, hours: e.target.value });
  };

  const handleStartMinutesChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onStartTimeChange({ ...startTime, minutes: e.target.value });
  };

  const handleStartSecondsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onStartTimeChange({ ...startTime, seconds: e.target.value });
  };

  const handleStartPeriodChange = (period: 'AM' | 'PM') => {
    onStartTimeChange({ ...startTime, period });
  };

  const handleEndHoursChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onEndTimeChange({ ...endTime, hours: e.target.value });
  };

  const handleEndMinutesChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onEndTimeChange({ ...endTime, minutes: e.target.value });
  };

  const handleEndSecondsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onEndTimeChange({ ...endTime, seconds: e.target.value });
  };

  const handleEndPeriodChange = (period: 'AM' | 'PM') => {
    onEndTimeChange({ ...endTime, period });
  };

  return (
    <div className='time-selector'>
      <div className='time-selector-container'>
        {(mode === 'start' || mode === 'both') && startTime && (
          <div className='time-section'>
            <div className='time-input-wrapper'>
              <div className='time-input-container'>
                <div className='time-input-inner'>
                  <CustomSelect value={startTime.hours} options={hours} onChange={handleStartHoursChange} />
                </div>
              </div>
            </div>
            <span className='time-separator'>:</span>
            <div className='time-input-wrapper'>
              <div className='time-input-container'>
                <div className='time-input-inner'>
                  <CustomSelect value={startTime.minutes} options={minutes} onChange={handleStartMinutesChange} />
                </div>
              </div>
            </div>
            <span className='time-separator'>:</span>
            <div className='time-input-wrapper'>
              <div className='time-input-container'>
                <div className='time-input-inner'>
                  <CustomSelect value={startTime.seconds} options={seconds} onChange={handleStartSecondsChange} />
                </div>
              </div>
            </div>
            <div className='period-toggle'>
              <button
                analytics-id='time-selector-start-am-btn'
                className={`period-button period-button-left ${startTime.period === 'AM' ? 'period-active' : ''}`}
                onClick={() => handleStartPeriodChange('AM')}
              >
                <span className={`period-text ${startTime.period === 'AM' ? 'period-text-active' : ''}`}>AM</span>
              </button>
              <button
                analytics-id='time-selector-start-pm-btn'
                className={`period-button period-button-right ${startTime.period === 'PM' ? 'period-active' : ''}`}
                onClick={() => handleStartPeriodChange('PM')}
              >
                <span className={`period-text ${startTime.period === 'PM' ? 'period-text-active' : ''}`}>PM</span>
              </button>
            </div>
          </div>
        )}
        {(mode === 'end' || mode === 'both') && endTime && (
          <div className='time-section'>
            <div className='time-input-wrapper'>
              <div className='time-input-container'>
                <div className='time-input-inner'>
                  <CustomSelect value={endTime.hours} options={hours} onChange={handleEndHoursChange} />
                </div>
              </div>
            </div>
            <span className='time-separator'>:</span>
            <div className='time-input-wrapper'>
              <div className='time-input-container'>
                <div className='time-input-inner'>
                  <CustomSelect value={endTime.minutes} options={minutes} onChange={handleEndMinutesChange} />
                </div>
              </div>
            </div>
            <span className='time-separator'>:</span>
            <div className='time-input-wrapper'>
              <div className='time-input-container'>
                <div className='time-input-inner'>
                  <CustomSelect value={endTime.seconds} options={seconds} onChange={handleEndSecondsChange} />
                </div>
              </div>
            </div>
            <div className='period-toggle'>
              <button
                analytics-id='time-selector-end-am-btn'
                className={`period-button period-button-left ${endTime.period === 'AM' ? 'period-active' : ''}`}
                onClick={() => handleEndPeriodChange('AM')}
              >
                <span className={`period-text ${endTime.period === 'AM' ? 'period-text-active' : ''}`}>AM</span>
              </button>
              <button
                analytics-id='time-selector-end-pm-btn'
                className={`period-button period-button-right ${endTime.period === 'PM' ? 'period-active' : ''}`}
                onClick={() => handleEndPeriodChange('PM')}
              >
                <span className={`period-text ${endTime.period === 'PM' ? 'period-text-active' : ''}`}>PM</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeSelector;
